import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink, hreflangPair } from "@/components/FiNav";
import { fiKontaktFaq, fiKontaktSections } from "@/data/fi-content";

const TITLE = "Yhteystiedot – AK Husbil & Fritid, Kalix (palvelemme suomeksi)";
const DESC =
  "Soita +46 70 862 97 43 tai tule käymään: Stabsvägen 5, Kalix. Torniosta 90 km. Vastaamme suomeksi 24 tunnin sisällä.";
const URL = "https://www.akhusbilfritid.se/fi/yhteystiedot";

export const Route = createFileRoute("/fi/yhteystiedot")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fiKontaktFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "AK Husbil & Fritid, Stabsvägen 5, Kalix" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangPair("/fi/yhteystiedot", "/kontakt")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Yhteystiedot – AK Husbil & Fritid",
          url: URL,
          inLanguage: "fi",
          mainEntity: { "@id": "#organization" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
    ],
  }),
  component: FiKontaktPage,
});

function FiKontaktPage() {
  return (
    <>
      <PageHeader
        label="Yhteystiedot · Vastaamme suomeksi"
        title={
          <>
            Soita, niin <span className="font-italic-display text-accent">jutellaan</span>.
          </>
        }
        sub="Myyjämme Aaron vastaa ja palvelee suomeksi. Aukioloajat sopimuksen mukaan — myös illat ja viikonloput onnistuvat, jotta ehdit tulla Suomen puolelta rauhassa."
      />

      <FiNav />

      <section className="container-edge py-12">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="space-y-3 md:col-span-5">
            <InfoBlock label="Käy meillä" icon={<MapPin size={20} />} primary="Stabsvägen 5, 952 51 Kalix" />
            <InfoBlock
              label="Myyjä · Aaron"
              icon={<Phone size={20} />}
              primary={
                <a href="tel:+46708629743" className="hover:text-accent">
                  +46 70 862 97 43
                </a>
              }
            />
            <InfoBlock
              label="Sähköposti"
              icon={<Mail size={20} />}
              primary={
                <a href="mailto:info@akhusbilfritid.se" className="hover:text-accent">
                  info@akhusbilfritid.se
                </a>
              }
            />
            <InfoBlock label="Aukiolo" icon={<Clock size={20} />} primary="Sopimuksen mukaan" />

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Lähetä viesti lomakkeella <ArrowRight size={14} />
            </Link>
          </div>

          <div className="md:col-span-7">
            <div className="overflow-hidden rounded-3xl border border-border soft-shadow">
              <iframe
                title="Kartta – Stabsvägen 5, Kalix"
                src="https://www.google.com/maps?q=Stabsv%C3%A4gen+5,+Kalix&output=embed"
                className="h-80 w-full"
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Torniosta noin 90 km E4-tietä Haaparannan kautta, Kemistä noin 115 km ja Oulusta noin 215 km. Huomaa
              aikaero: Ruotsissa kello on tunnin Suomen aikaa jäljessä.
            </p>
          </div>
        </div>
      </section>

      <HubEditorial
        eyebrow="Käytännön tiedot"
        sections={fiKontaktSections}
        faq={fiKontaktFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <FiSwedishLink to="/kontakt" label="Kontakt på svenska" />
    </>
  );
}

function InfoBlock({ label, icon, primary }: { label: string; icon: React.ReactNode; primary: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs font-medium uppercase tracking-wider text-accent">{label}</p>
      <div className="mt-3 flex items-center gap-3 font-display text-xl md:text-2xl">
        <span className="text-accent">{icon}</span> {primary}
      </div>
    </div>
  );
}
